import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';
import { formatWhen } from './familyFormat';
import { openFamilyReport } from './openFamilyReport';
import SessionClassPhotos from '../../components/Teacher/SessionClassPhotos';
import AuthImage from '../../components/AuthImage';
import { resolvePublicAssetUrl } from '../../services/api';

export default function FamilyClassDetail() {
  const { sessionId } = useParams();
  const { child } = useFamilyChild();
  const navigate = useNavigate();
  const q = useQuery(
    ['familySession', child.studentId, sessionId],
    async () => (await parentApi.session(child.studentId, sessionId)).data?.data,
    { enabled: !!child.studentId && !!sessionId }
  );
  const d = q.data;

  if (q.isLoading || !d) {
    return q.isLoading ? <CircularProgress /> : <Typography color="text.secondary">Class not found.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6">{formatWhen(d)}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {[d.attendanceLabel, d.teacherName, d.batchName].filter(Boolean).join(' · ')}
      </Typography>

      {d.recap && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">What we did</Typography>
          <Typography color="text.secondary">{d.recap}</Typography>
        </Box>
      )}

      {(d.photos || []).length > 0 ? (
        <SessionClassPhotos sessionId={sessionId} photos={d.photos} canManage={false} />
      ) : null}

      {d.homework && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Homework · {d.homework.statusLabel}</Typography>
            <Typography sx={{ mt: 0.5 }}>
              {d.homework.instruction || (d.homework.images?.length ? 'Screenshot from this class' : 'Homework from this class')}
            </Typography>
            {(d.homework.images || []).length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {d.homework.images.map((img) => (
                  <AuthImage
                    key={img.id}
                    src={img.url}
                    alt={img.name || 'Homework screenshot'}
                    style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 8 }}
                  />
                ))}
              </Box>
            ) : null}
            {d.homework.teacherFileUrl ? (
              <Button href={resolvePublicAssetUrl(d.homework.teacherFileUrl)} target="_blank" rel="noreferrer" sx={{ mt: 1 }}>
                Teacher file
              </Button>
            ) : null}
            {d.homework.status === 'todo' && (
              <Button sx={{ mt: 1 }} variant="contained" onClick={() => navigate(`/family/homework/${d.sessionId}`)}>
                Do it now
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {d.report && (
        <Button
          fullWidth
          variant="outlined"
          sx={{ mb: 2, justifyContent: 'flex-start' }}
          onClick={() => openFamilyReport(child.studentId, d.report.reportId)}
        >
          {d.report.monthLabel} report · View
        </Button>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>About this class</Typography>
      <Button fullWidth variant="outlined" sx={{ mb: 1, justifyContent: 'flex-start' }} onClick={() => navigate(`/family/messages/new?channel=teacher&batchId=${encodeURIComponent(d.batchId || '')}`)}>
        Message {d.teacherName || 'the teacher'}
      </Button>
      <Button fullWidth variant="outlined" sx={{ mb: 1, justifyContent: 'flex-start' }} onClick={() => navigate('/family/messages/new?channel=rm&threadType=complaint')}>
        Report a problem
      </Button>
      {d.canMiss && (
        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate(`/family/leave?sessionId=${encodeURIComponent(d.sessionId)}`)}
        >
          Can't attend
        </Button>
      )}
    </Box>
  );
}
